import styled from 'styled-components';
import { device, mtrStyle } from 'src/styles/global';
import theme from 'src/styles/theme';

export interface Props {
  $active?: boolean;
}

/* DESKTOP */
export const InsightsWrapperDesktop = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap; /* 1–3 cards: single row, no scroll */
  align-items: stretch;
  gap: 24px;
  padding: 12px 8px 24px; /* small side padding so shadows don't clip on 1–3 */

  /* Hide on mobile */
  @media ${device.mobile} {
    display: none;
  }

  /*  Desktop default: 3-up */
  > * {
    flex: 0 0 calc((100% - 48px) / 3);
    max-width: calc((100% - 48px) / 3);
  }

  @supports selector(:has(*)) {
    /* Exactly 2 cards 50/50 */
    &:has(> *:nth-child(2)):not(:has(> *:nth-child(3))) {
      padding-left: 4px;
      padding-right: 4px;
      margin-left: -4px;
      margin-right: -4px;
    }

    &:has(> *:nth-child(2)):not(:has(> *:nth-child(3))) > * {
      flex: 0 0 calc((100% - 24px) / 2);
      max-width: calc((100% - 24px) / 2);
    }

    /* Exactly 3 cards: widen the row slightly while keeping shadow room */
    &:has(> *:nth-child(3)):not(:has(> *:nth-child(4))) {
      padding-left: 4px;
      padding-right: 4px;
      margin-left: -4px;
      margin-right: -4px;
    }

    /* Exactly 1 card full width */
    &:not(:has(> *:nth-child(2))) {
      padding-left: 4px;
      padding-right: 4px;
      margin-left: -4px;
      margin-right: -4px;
    }

    &:not(:has(> *:nth-child(2))) > * {
      flex: 0 0 100%;
      max-width: 100%;
    }

    /* 4+ cards horizontal scroller with 344px cards */
    &:has(> *:nth-child(4)) {
      overflow-x: auto;
      overflow-y: hidden;
      flex-wrap: nowrap;
      -webkit-overflow-scrolling: touch;

      /* hide scrollbar */
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }

      /* snap + edge breathing space */
      scroll-snap-type: x mandatory;
      scroll-padding: 0 16px;
      padding: 12px 0px 24px; /* remove side padding while scrolling */

      /* right-side bump */
      margin-right: -8px;
      padding-right: 0px;

      /* left-side bump */
      margin-left: -8px;
      padding-left: 0px;
    }

    &:has(> *:nth-child(4)) > * {
      flex: 0 0 344px;
      max-width: 344px;
      min-width: 344px;
      scroll-snap-align: center;
    }

    &:has(> *:nth-child(4)) > :first-child {
      margin-left: 8px;
    }
    &:has(> *:nth-child(4)) > :last-child {
      margin-right: 8px;
    }
  }

  /* Safari fallbacks for exact 1 / 2 / 3 card widths */
  > :only-child,
  > :first-child:last-child {
    flex: 0 0 100%;
    max-width: 100%;
  }

  &:has(> :only-child),
  &:has(> :first-child:last-child) {
    padding-left: 4px;
    padding-right: 4px;
    margin-left: -4px;
    margin-right: -4px;
  }

  &:has(> :only-child) > * > *,
  &:has(> :first-child:last-child) > * > * {
    @media (hover: hover) and (pointer: fine) {
      &:hover,
      &:active {
        transform: none;
      }
    }

    &:focus-visible {
      transform: none;
    }
  }
  > :first-child:nth-last-child(2),
  > :first-child:nth-last-child(2) ~ * {
    flex: 0 0 calc((100% - 24px) / 2);
    max-width: calc((100% - 24px) / 2);
  }
  > :first-child:nth-last-child(3),
  > :first-child:nth-last-child(3) ~ * {
    flex: 0 0 calc((100% - 48px) / 3);
    max-width: calc((100% - 48px) / 3);
  }

  /* 4+ cards keep all scroller/bumps via fallback too */
  overflow-x: auto; /* allows scrolling when 4+ */
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  flex-wrap: nowrap;

  /* hide scrollbar (Safari fallback) */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* apply fixed widths only when there are 4+ children */
  > :first-child:nth-last-child(n + 4),
  > :first-child:nth-last-child(n + 4) ~ * {
    flex: 0 0 344px;
    max-width: 344px;
    min-width: 344px;
    scroll-snap-align: center;
  }

  /* snap always on, but side padding is removed in the 4+ block above */
  scroll-snap-type: x mandatory;
  scroll-padding: 0 16px;

  /* Tablet and iPad */
  @media ${device.tablet}, ${device.ipad} {
    gap: 16px;
    padding: 0px 8px 16px; /* small side padding on tablet for 1–3 */

    /* 3-up default with smaller gap */
    > * {
      flex: 0 0 calc((100% - 32px) / 3);
      max-width: calc((100% - 32px) / 3);
    }

    @supports selector(:has(*)) {
      /* 2-up */
      &:has(> *:nth-child(2)):not(:has(> *:nth-child(3))) {
        padding-left: 4px;
        padding-right: 4px;
        margin-left: -4px;
        margin-right: -4px;
      }

      &:has(> *:nth-child(2)):not(:has(> *:nth-child(3))) > * {
        flex: 0 0 calc((100% - 16px) / 2);
        max-width: calc((100% - 16px) / 2);
      }

      /* Exactly 3 cards: widen the row slightly while keeping shadow room */
      &:has(> *:nth-child(3)):not(:has(> *:nth-child(4))) {
        padding-left: 4px;
        padding-right: 4px;
        margin-left: -4px;
        margin-right: -4px;
      }

      /* 1-up */
      &:not(:has(> *:nth-child(2))) {
        padding-left: 4px;
        padding-right: 4px;
        margin-left: -4px;
        margin-right: -4px;
      }

      &:not(:has(> *:nth-child(2))) > * {
        flex: 0 0 100%;
        max-width: 100%;
      }

      /* 4+ scroller: keep 344px cards */
      &:has(> *:nth-child(4)) {
        padding: 4px 0px 16px; /* remove side padding while scrolling */
        scroll-padding: 0 16px;

        /* right-side bump */
        margin-right: -16px;
        padding-right: 0px;

        /* left-side bump */
        margin-left: -16px;
        padding-left: 0px;

        overflow-y: hidden;
      }
      &:has(> *:nth-child(4)) > * {
        flex: 0 0 344px;
        max-width: 344px;
        min-width: 344px;
      }
      &:has(> *:nth-child(4)) > :first-child {
        margin-left: 0px;
      }
      &:has(> *:nth-child(4)) > :last-child {
        margin-right: 0px;
      }

      /* extend first/last card so they peek outside the container */
      &:has(> *:nth-child(4)) > :first-child {
        margin-left: 16px;
      }
      &:has(> *:nth-child(4)) > :last-child {
        margin-right: 16px;
      }
    }

    /* Safari fallbacks for tablet widths 1 / 2 / 3 */
    > :only-child,
    > :first-child:last-child {
      flex: 0 0 100%;
      max-width: 100%;
    }
    > :first-child:nth-last-child(2),
    > :first-child:nth-last-child(2) ~ * {
      flex: 0 0 calc((100% - 16px) / 2);
      max-width: calc((100% - 16px) / 2);
    }
    > :first-child:nth-last-child(3),
    > :first-child:nth-last-child(3) ~ * {
      flex: 0 0 calc((100% - 32px) / 3);
      max-width: calc((100% - 32px) / 3);
    }

    /* 4+ only: strip + hidden scrollbar + 344px cards (tablet) */
    > :first-child:nth-last-child(n + 4),
    > :first-child:nth-last-child(n + 4) ~ * {
      flex: 0 0 344px;
      max-width: 344px;
      min-width: 344px;
      scroll-snap-align: center;
    }
    /* hide scrollbar (Safari fallback tablet) */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
    &::-webkit-scrollbar-thumb {
      background: transparent;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
`;

export const InsightsContainerDesktop = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
`;

export const InsightsCardDesktop = styled.div<Props>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: ${mtrStyle.borderRadius};
  background-color: ${theme.colors.white0};
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.25);
  height: 100%;
  min-height: 180px;

  border: ${({ $active }) =>
    $active
      ? `2px solid ${theme.colors.mtr60}` // highlight border for selected state
      : '2px solid transparent'};

  /* Smooth transition */
  transition: background-color 140ms ease,
    transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 140ms ease;
  transform: translateZ(0);
  transform-origin: center;

  /* Hover-capable pointers */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: #f2f2fd;
      transform: scale(0.99);
    }
    &:active {
      transform: scale(0.986);
    }
  }

  /* Keyboard */
  &:focus-visible {
    outline: none;
    background-color: #f2f2fd;
    transform: scale(0.99);
  }

  /* Motion sensitivity */
  @media (prefers-reduced-motion: reduce) {
    transition: background-color 0.01s linear;
    transform: none !important;
  }
`;

/* MOBILE */
export const InsightsWrapperMobile = styled.div`
  display: none;

  @media ${device.mobile} {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 16px;
    padding: 8px 0 8px 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;

    /* hide scrollbar */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }

    /* snap scrolling */
    scroll-snap-type: x mandatory;
    scroll-padding: 0 16px;
  }
`;

export const InsightsContainerMobile = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 90%; /* default: 90% viewport; single card: 95% */
  scroll-snap-align: center;

  &:only-child {
    flex: 0 0 100%;
    margin-left: 0;
    margin-right: 0;
  }

  &:first-child {
    margin-left: 8px;
  }
  &:last-child {
    margin-right: 8px;
  }
`;

export const InsightsCardMobile = styled.div<Props>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  border-radius: ${mtrStyle.borderRadius};
  background-color: ${({ $active }) =>
    $active ? '#F2F2FD' : theme.colors.white0};
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.25);
  height: 100%;
  min-height: 180px;
  border: ${({ $active }) =>
    $active
      ? `2px solid ${theme.colors.mtr60}` // highlight border for selected state
      : '2px solid transparent'};
  transition: background-color 140ms ease;
`;

export const CardTypeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;

  & > svg {
    width: 48px;
    height: 48px;
    display: block;
    flex: 0 0 auto;
  }
`;

export const CardType = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${theme.colors.neutralW20};
`;

export const CardStatus = styled.div`
  font-size: 16px;
  color: ${theme.colors.neutralW40};
  position: relative;

  .hover-content,
  .default-content {
    transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: opacity;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
  }
  .default-content {
    opacity: 1;
    z-index: 1;
    position: relative;
  }
  .hover-content {
    opacity: 0;
    pointer-events: none;
    z-index: 2;
  }

  /* Show hover content when parent card is hovered */
  .momentum-card:hover & .default-content,
  .peakairplay-card:hover & .default-content,
  .rotation-card:hover & .default-content,
  .region-card:hover & .default-content,
  .genre-card:hover & .default-content {
    opacity: 0;
  }
  .momentum-card:hover & .hover-content,
  .peakairplay-card:hover & .hover-content,
  .rotation-card:hover & .hover-content,
  .region-card:hover & .hover-content,
  .genre-card:hover & .hover-content {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const CardSpacingMobile = styled.div`
  padding-bottom: 16px;
`;

export const ViewInsightButton = styled.button<{ $active?: boolean }>`
  font-size: 14px;
  color: ${theme.colors.mtr60};
  border: none;
  background: ${({ $active }) => ($active ? '#F2F2FD' : 'transparent')};
  cursor: pointer;
  padding: 2px 8px 2px 0; /* flush left; small top/right/bottom padding */
  margin-top: auto;
  text-align: left;
  align-self: flex-start;
  border-radius: 6px;
  transition: background-color 140ms ease;

  &:focus-visible {
    outline: 2px solid ${theme.colors.mtr60};
    outline-offset: 2px;
  }
`;

export const OverviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const OverviewInsightsStyle = styled.div`
  font-size: 14px;
  color: ${theme.colors.neutralW40};
`;
